import { formatTimestampDateTime } from './dates';

const MINUTE_MS = 60_000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;
const WEEK_MS = 7 * DAY_MS;

export type CommentDto = {
  id: string;
  body: string;
  actor_id: string | null;
  actor_kind: string;
  edited_at: string | null;
  created_at: string;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

export function isCommentDto(value: unknown): value is CommentDto {
  return (
    isRecord(value) &&
    typeof value.id === 'string' &&
    typeof value.body === 'string' &&
    (value.actor_id === null || typeof value.actor_id === 'string') &&
    typeof value.actor_kind === 'string' &&
    (value.edited_at === null || typeof value.edited_at === 'string') &&
    typeof value.created_at === 'string'
  );
}

function plural(count: number, noun: string): string {
  return count === 1 ? `1 ${noun} ago` : `${count} ${noun}s ago`;
}

/** Relative time for recent comments; locale datetime once a week has passed. */
export function formatCommentWhen(iso: string, now: Date = new Date(), locale?: string): string {
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return iso;
  const diff = now.getTime() - parsed.getTime();
  if (diff < 0) return formatTimestampDateTime(iso, locale) ?? iso;
  if (diff < MINUTE_MS) return 'Just now';
  if (diff < HOUR_MS) return plural(Math.floor(diff / MINUTE_MS), 'minute');
  if (diff < DAY_MS) return plural(Math.floor(diff / HOUR_MS), 'hour');
  if (diff < WEEK_MS) {
    const days = Math.floor(diff / DAY_MS);
    return days === 1 ? 'Yesterday' : plural(days, 'day');
  }
  return formatTimestampDateTime(iso, locale) ?? iso;
}
